import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import PillarsSection from "./PillarsSection";
import "../styles/Home.css";
import "../styles/GrowthStories.css";
import "../styles/GrowWithUs.css";

const stageData = {
  seed: {
    heading: "Laying the Groundwork",
    description:
      "You're focused on building a solid foundation and defining the basics for sustainable growth.",
    image: "static/images/Home/flowerOne.png",
  },
  seedling: {
    heading: "Gaining Traction",
    description:
      "You've started to see progress and are working to build momentum in your journey.",
    image: "static/images/Home/flowerTwo.png",
  },
  plant: {
    heading: "Scaling Up",
    description:
      "Your operations are growing, and you're looking to expand your reach and influence in the market.",
    image: "static/images/Home/flowerThree.png",
  },
  tree: {
    heading: "Securing your Market Position",
    description:
      "You’re an established player, working to strengthen your leadership and maintain your competitive edge.",
    image: "static/images/Home/flowerFour.png",
  },
};

const Home = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [selectedStage, setSelectedStage] = useState("tree");
  // Once the user clicks "This is me", we mark the stage as confirmed
  const [stageConfirmed, setStageConfirmed] = useState(false);
  const [userName, setUserName] = useState("");
  const [businessLink, setBusinessLink] = useState("");
  const [email, setEmail] = useState("");
  const [thankYouVisible, setThankYouVisible] = useState(false);
  // State controlling the fade in of top text and bottom text
  const [showTopText, setShowTopText] = useState(false);
  const [showBottomText, setShowBottomText] = useState(false);
  // A flag to ensure the animation only starts once
  const [hasStarted, setHasStarted] = useState(false);

  const [imageSrc, setImageSrc] = useState(stageData[selectedStage].image);
  const [fadeClass, setFadeClass] = useState("fade-in");

  const homeRef = useRef(null);
  const treeSectionRef = useRef(null);
  const pillarsRef = useRef(null);
  const growthStoriesRef = useRef(null);
  const growWithUsRef = useRef(null);
  const formDetailsRef = useRef(null);
  const thankYouRef = useRef(null);
  const videoRef = useRef(null);

  const [homeInView, setHomeInView] = useState(false);
  const [treeInView, setTreeInView] = useState(false);
  const [pillarsInView, setPillarsInView] = useState(false);
  const [growthStoriesInView, setGrowthStoriesInView] = useState(false);
  const [growWithUsInView, setGrowWithUsInView] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (location.hash === "#growth-stories" && growthStoriesRef.current) {
      growthStoriesRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (location.hash === "#grow-with-us" && growWithUsRef.current) {
      growWithUsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // If user scrolled into the tree section and we haven't started yet, do so now
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);

          // 1) Fade in top text
          setShowTopText(true);

          // 2) After 1.5s, start video playback
          setTimeout(() => {
            if (videoRef.current) {
              videoRef.current.play();
            }
          }, 1500);
        }
      },
      {
        root: null, // viewport
        rootMargin: "0px",
        threshold: 0.5, // 50% in view to trigger
      }
    );

    if (treeSectionRef.current) {
      observer.observe(treeSectionRef.current);
    }

    return () => {
      if (treeSectionRef.current) {
        observer.unobserve(treeSectionRef.current);
      }
    };
  }, [hasStarted]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target;

          if (target === homeRef.current) {
            setHomeInView(entry.isIntersecting);
          }
          if (target === treeSectionRef.current) {
            setTreeInView(entry.isIntersecting);
          }
          if (target === pillarsRef.current) {
            setPillarsInView(entry.isIntersecting);
          }
          if (target === growthStoriesRef.current) {
            setGrowthStoriesInView(entry.isIntersecting);
          }
          if (target === growWithUsRef.current) {
            setGrowWithUsInView(entry.isIntersecting);
          }
        });
      },
      {
        threshold: 0.2,
        // Use negative rootMargin on desktop only
        rootMargin: isMobile ? "0px 0px 0px 0px" : "-104px 0px 0px 0px",
      }
    );

    if (homeRef.current) observer.observe(homeRef.current);
    if (treeSectionRef.current) observer.observe(treeSectionRef.current);
    if (pillarsRef.current) observer.observe(pillarsRef.current);
    if (growthStoriesRef.current) observer.observe(growthStoriesRef.current);
    if (growWithUsRef.current) observer.observe(growWithUsRef.current);

    return () => {
      if (homeRef.current) observer.unobserve(homeRef.current);
      if (treeSectionRef.current) observer.unobserve(treeSectionRef.current);
      if (pillarsRef.current) observer.unobserve(pillarsRef.current);
      if (growthStoriesRef.current)
        observer.unobserve(growthStoriesRef.current);
      if (growWithUsRef.current) observer.unobserve(growWithUsRef.current);
    };
  }, [isMobile]);

  useEffect(() => {
    // Trigger a fade-out
    setFadeClass("fade-out");

    const timeout = setTimeout(() => {
      // Once fade-out is done, change the source
      setImageSrc(stageData[selectedStage].image);

      // Then fade in the new image
      setFadeClass("fade-in");
    }, 300); // This 300ms matches the CSS transition duration

    return () => clearTimeout(timeout);
  }, [selectedStage]);

  // Called when video ends -> show bottom text
  const handleVideoEnd = () => {
    setShowBottomText(true);
  };

  // Called when user clicks "This is me"
  const handleStageConfirm = () => {
    setStageConfirmed(true);
    setTimeout(() => {
      if (formDetailsRef.current) {
        const yOffset = -250; // Same 200px offset
        const y =
          formDetailsRef.current.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 100);
  };

  // Email validation function
  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Whether the "Let’s Grow" button should be enabled
  const canGrow =
    stageConfirmed &&
    userName.trim() &&
    businessLink.trim() &&
    isEmailValid(email);

  // Smooth scroll to the thank you text
  const scrollToThankYou = () => {
    setTimeout(() => {
      if (thankYouRef.current) {
        const yOffset = -200; // Adjust this value as needed
        const y =
          thankYouRef.current.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 100);
  };

  // Called when user hits "Let's Grow" -> send email, show thank you
  const handleSubmit = async () => {
    if (!canGrow) return;

    try {
      // Get country name from IP address
      let country = "Unknown";
      try {
        const ipResponse = await fetch("https://ipapi.co/json/");
        const ipData = await ipResponse.json();
        country = ipData.country_name || "Unknown";
      } catch (ipError) {
        console.error("Error detecting country:", ipError);
      }

      const response = await fetch("/api/sendEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userName,
          businessLink,
          email,
          stage: selectedStage,
          country, // Now contains full country name
        }),
      });

      if (response.ok) {
        setThankYouVisible(true);
        scrollToThankYou();
      } else {
        console.error("Error sending email");
      }
    } catch (err) {
      console.error("Error sending email:", err);
    }
  };

  return (
    <>
      {/* Existing full-height hero section */}
      <div
        ref={homeRef}
        className={`
          home-main-wrapper
          fade-section-initial
          ${homeInView ? "in-view" : "out-of-view"}
        `}
      >
        {" "}
        {!isMobile ? (
          // Desktop Structure (Three Columns)
          <div className="home-container desktop-layout">
            <div className="text-column">
              We
              <br />
              Help
              <br />
              You
            </div>
            <div className="image-column">
              <video
                src="static/videos/mainAnimation.mp4"
                alt="Flower Illustration"
                autoPlay
                muted
                playsInline
                webkit-playsinline="true"
                className="flower-image"
              />
            </div>
            <div className="text-column bold-text">Grow</div>
          </div>
        ) : (
          // Mobile Structure (Three Rows)
          <div className="home-container mobile-layout">
            <div className="text-row">We Help You</div>
            <div className="text-row bold-text">Grow</div>
            <div className="image-row">
              <video
                src="static/videos/mainAnimation.mp4"
                alt="Flower Illustration"
                autoPlay
                muted
                playsInline
                webkit-playsinline="true"
                className="flower-image"
              />
            </div>
          </div>
        )}
      </div>
      <div
        ref={treeSectionRef}
        className={`
          tree-video-section
          fade-section
          ${treeInView ? "in-view" : "out-of-view"}
        `}
      >
        {" "}
        {/* Top text that fades in first */}
        <div className={`top-text ${showTopText ? "fade-in" : ""}`}>
          <p>
            <strong>Sustainably</strong>, balancing immediate impact with
            lasting value, adapting to change, and building a strong foundation
            together.
          </p>
        </div>
        {/* The video itself */}
        <video
          ref={videoRef}
          src="static/images/treeAnimation.mp4"
          className="tree-video"
          muted
          autoPlay
          playsInline
          webkit-playsinline="true"
          onEnded={handleVideoEnd}
        />
        {/* Bottom text: hidden until video ends */}
        <div className={`bottom-text ${showBottomText ? "fade-in" : ""}`}>
          <h3>
            Like a<br /> <span className="highlight">plant.</span>
          </h3>
          <p>Steady, natural, purposeful.</p>
        </div>
      </div>
      <div
        ref={pillarsRef}
        className={`
          fade-section
          ${pillarsInView ? "in-view" : "out-of-view"}
        `}
      >
        <PillarsSection />
      </div>
      {/* Growth Stories Section */}
      {!isMobile ? (
        <section
          id="growth-stories"
          ref={growthStoriesRef}
          className={`
        growth-stories-section
        fade-section
        ${growthStoriesInView ? "in-view" : "out-of-view"}
      `}
        >
          <h2>Growth Stories</h2>

          <div className="growth-stories-container">
            {/* Three-column layout */}
            <div className="gs-three-cols">
              {/* --- Column 1 --- */}
              <div className="gs-col col-1">
                <img
                  src="static/images/icon-mountain.png"
                  alt="Mountain Icon"
                  className="col-icon"
                />
                <h3>1</h3>
                <h4>Challenge</h4>
                <p>
                  Mandatory compliance with MICA and DORA by June 2025 to
                  operate legally in Europe.
                </p>
              </div>

              {/* --- Column 2 --- */}
              <div className="gs-col col-2">
                <div className="gs-header-2">
                  <img
                    src="static/images/logoPlaceholder.png"
                    alt="Example Logo"
                    className="fs-logo"
                  />
                  <h4>Financial Services</h4>
                </div>
                <div className="gs-header-2b">
                  <h3>2</h3>
                  <h4>Problem</h4>
                  <p>
                    Non-compliance would result in restricted operations across
                    the European market.
                  </p>
                </div>
                <img
                  src="static/images/icon-problem.png"
                  alt="Problem Icon"
                  className="col-icon"
                />
              </div>

              {/* --- Column 3 --- */}
              <div className="gs-col col-3">
                <img
                  src="static/images/icon-lightbulb.png"
                  alt="Lightbulb Icon"
                  className="col-icon"
                />
                <h3>3</h3>
                <h4>Solution</h4>
                <p>
                  Using our MICA/DORA management service, the client
                  successfully achieved approval to operate legally in Europe.
                </p>
              </div>

              {/* The blue line in the middle of the three-column container */}
              <img
                src="static/images/blue-line.png"
                alt="Timeline"
                className="blue-line"
              />
            </div>

            {/* Buttons at the bottom */}
            <div className="gs-buttons">
              <button className="gs-button blue-button">
                Same Challenge?
                <br /> Let’s Talk
              </button>
              <button className="gs-button outline-button">
                Discover
                <br /> the Story
              </button>
            </div>
          </div>
        </section>
      ) : (
        // Mobile Growth Stories
        <section
          id="growth-stories"
          ref={growthStoriesRef}
          className={`
        growth-stories-section-mobile
        fade-section
        ${growthStoriesInView ? "in-view" : "out-of-view"}
      `}
        >
          <h2>Growth Stories</h2>
          <div className="growth-stories-container-mobile">
            {/* --- Mobile Header (from gs-header-2) --- */}
            <div className="gs-header-2-mobile">
              <img
                src="static/images/logoPlaceholder.png"
                alt="Example Logo"
                className="fs-logo-mobile"
              />
              <h4>Financial Services</h4>
            </div>

            {/* --- Mobile Rows Container --- */}
            <div className="gs-mobile-rows">
              {/* Row 1 – Content from gs-col.col-1 (image on right) */}
              <div className="gs-mobile-row gs-mobile-row-1">
                <div className="gs-mobile-text-left">
                  <h3>1</h3>
                  <h4>Challenge</h4>
                  <p>
                    Mandatory compliance with MICA and DORA by June 2025 to
                    operate legally in Europe.
                  </p>
                </div>
                <div className="gs-mobile-image-left">
                  <img
                    src="static/images/icon-mountain.png"
                    alt="Mountain Icon"
                    className="col-icon"
                  />
                </div>
              </div>

              {/* Row 2 – Content from gs-col.col-2 (image on left) */}
              <div className="gs-mobile-row gs-mobile-row-2">
                <div className="gs-mobile-text-right">
                  <h3>2</h3>
                  <h4>Problem</h4>
                  <p>
                    Non-compliance would result in restricted operations across
                    the European market.
                  </p>
                </div>
                <div className="gs-mobile-image-right">
                  <img
                    src="static/images/icon-problem.png"
                    alt="Problem Icon"
                    className="col-icon"
                  />
                </div>
              </div>

              {/* Row 3 – Content from gs-col.col-3 (image on right) */}
              <div className="gs-mobile-row gs-mobile-row-3">
                <div className="gs-mobile-text-left">
                  <h3>3</h3>
                  <h4>Solution</h4>
                  <p>
                    Using our MICA/DORA management service, the client
                    successfully achieved approval to operate legally in Europe.
                  </p>
                </div>
                <div className="gs-mobile-image-left">
                  <img
                    src="static/images/icon-lightbulb.png"
                    alt="Lightbulb Icon"
                    className="col-icon"
                  />
                </div>
              </div>
              <img
                src="static/images/blue-line-mobile.png"
                alt="Timeline"
                className="blue-line-mobile"
              />
            </div>

            {/* --- Mobile Buttons at the Bottom --- */}
            <div className="gs-buttons">
              <button className="gs-button blue-button">
                Same Challenge?
                <br /> Let’s Talk
              </button>
              <button className="gs-button outline-button">
                Discover
                <br /> the Story
              </button>
            </div>
          </div>
        </section>
      )}
      {/* Grow With Us Section */}
      {!isMobile ? (
        <section
          id="grow-with-us"
          ref={growWithUsRef}
          className={`
        grow-with-us-section
        fade-section
        ${growWithUsInView ? "in-view" : "out-of-view"}
      `}
        >
          <h2>Grow With Us</h2>
          <div className="grow-with-us-container">
            <p className="stage-selector-instruction">1. Select your stage</p>

            <div className="grow-with-us-flex">
              {/* Stage image */}
              <div className="stage-image-container">
                <img
                  src={imageSrc}
                  alt={`${selectedStage} illustration`}
                  className={`stage-image ${fadeClass}`}
                />
              </div>

              {/* Stages list */}
              <ul className="stage-list">
                {Object.keys(stageData).map((stageKey) => (
                  <li
                    key={stageKey}
                    onClick={() => {
                      setSelectedStage(stageKey);
                      setStageConfirmed(false); // if they pick a new stage, reset confirmation
                    }}
                    className={selectedStage === stageKey ? "active" : ""}
                  >
                    {stageKey.charAt(0).toUpperCase() + stageKey.slice(1)}
                  </li>
                ))}
              </ul>

              {/* Divider */}
              <div className="vertical-divider" />

              {/* Right panel */}
              <div className="right-panel">
                <p className="you-are-label">You are…</p>
                <h3>{stageData[selectedStage].heading}</h3>
                <p className="description-text">
                  {stageData[selectedStage].description}
                </p>

                {/* "This is me" button */}
                <button
                  className="this-is-me-button"
                  style={{
                    backgroundColor: stageConfirmed ? "#0E436C" : "#1292e0", // dark blue if confirmed
                  }}
                  onClick={handleStageConfirm}
                >
                  This is me
                </button>
              </div>
            </div>

            {/* SUBSECTION 2: user details */}
            <p className="stage-selector-instruction">
              2. Fill in your details
            </p>

            {/* If user has not confirmed stage, show an inline message or keep the button disabled */}
            <div ref={formDetailsRef} className="details-form">
              <label>Your Name</label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value.slice(0, 50))}
                placeholder=""
              />

              <label>Business Link</label>
              <input
                type="text"
                value={businessLink}
                onChange={(e) => setBusinessLink(e.target.value.slice(0, 80))}
                placeholder=""
              />

              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder=""
                className={isEmailValid(email) ? "" : "invalid"}
              />

              <div className="lets-grow-button-container">
                <button
                  className="lets-grow-button"
                  onClick={handleSubmit}
                  disabled={!canGrow}
                  style={{
                    backgroundColor: canGrow ? "#1292e0" : "#ccc",
                    cursor: canGrow ? "pointer" : "not-allowed",
                  }}
                >
                  Let’s Grow
                </button>
                {!stageConfirmed && (
                  <p className="warning-text">
                    Please select your stage first.
                  </p>
                )}
              </div>
            </div>

            {thankYouVisible && (
              <div ref={thankYouRef} className="thank-you-section">
                <h1>
                  Thank you,
                  <br /> {userName}!
                </h1>
                <p>Check your mail for the next steps.</p>
              </div>
            )}
          </div>
        </section>
      ) : (
        // Mobile Grow With Us
        <section
          id="grow-with-us"
          ref={growWithUsRef}
          className={`
        grow-with-us-section-mobile
        fade-section
        ${growWithUsInView ? "in-view" : "out-of-view"}
      `}
        >
          <h2>Grow With Us</h2>
          <div className="grow-with-us-container">
            <p className="stage-selector-instruction">1. Select your stage</p>

            <div className="grow-with-us-flex">
              {/* Stage image */}
              <div className="stage-image-container">
                <img
                  src={stageData[selectedStage].image}
                  alt={`${selectedStage} illustration`}
                  className="stage-image"
                />
              </div>

              {/* Stages list */}
              <ul className="stage-list">
                {Object.keys(stageData).map((stageKey) => (
                  <li
                    key={stageKey}
                    onClick={() => {
                      setSelectedStage(stageKey);
                      setStageConfirmed(false); // if they pick a new stage, reset confirmation
                    }}
                    className={selectedStage === stageKey ? "active" : ""}
                  >
                    {stageKey.charAt(0).toUpperCase() + stageKey.slice(1)}
                  </li>
                ))}
              </ul>

              {/* Divider */}
              <div className="vertical-divider" />

              {/* Right panel */}
              <div className="right-panel">
                <p className="you-are-label">You are…</p>
                <h3>{stageData[selectedStage].heading}</h3>
                <p className="description-text">
                  {stageData[selectedStage].description}
                </p>

                {/* "This is me" button */}
                <button
                  className="this-is-me-button"
                  style={{
                    backgroundColor: stageConfirmed ? "#0E436C" : "#1292e0", // dark blue if confirmed
                  }}
                  onClick={handleStageConfirm}
                >
                  This is me
                </button>
              </div>
            </div>

            {/* SUBSECTION 2: user details */}
            <p className="stage-selector-instruction">
              2. Fill in your details
            </p>

            {/* If user has not confirmed stage, show an inline message or keep the button disabled */}
            <div ref={formDetailsRef} className="details-form">
              <label>Your Name</label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value.slice(0, 50))}
                placeholder=""
              />

              <label>Business Link</label>
              <input
                type="text"
                value={businessLink}
                onChange={(e) => setBusinessLink(e.target.value.slice(0, 80))}
                placeholder=""
              />

              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder=""
                className={isEmailValid(email) ? "" : "invalid"}
              />

              <div className="lets-grow-button-container">
                <button
                  className="lets-grow-button"
                  onClick={handleSubmit}
                  disabled={!canGrow}
                  style={{
                    backgroundColor: canGrow ? "#1292e0" : "#ccc",
                    cursor: canGrow ? "pointer" : "not-allowed",
                  }}
                >
                  Let’s Grow
                </button>
                {!stageConfirmed && (
                  <p className="warning-text">
                    Please select your stage first.
                  </p>
                )}
              </div>
            </div>

            {thankYouVisible && (
              <div ref={thankYouRef} className="thank-you-section">
                <h1>
                  Thank you,
                  <br /> {userName}!
                </h1>
                <p>Check your mail for the next steps.</p>
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
};

export default Home;
